import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckCircle2, Loader2, Heart, ShieldCheck, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAMS = ['Infant Care', 'Toddler Care', 'Preschool', 'Pre-K', 'After School'];

export default function PublicEnrollmentForm() {
  const { tenant_slug } = useParams();
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source') || 'website';

  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_name: '',
    child_age: '',
    program_interest: '',
    desired_start_date: '',
    message: '',
  });

  useEffect(() => {
    if (!tenant_slug) return;
    Promise.all([
      axios.get(`${API}/tenants/by-slug/${tenant_slug}/login-config`),
    ]).then(async ([cfgRes]) => {
      setTenant(cfgRes.data);
      try {
        const b = await axios.get(`${API}/branding/${cfgRes.data.tenant_id}`);
        setBranding(b.data);
      } catch { /* ignore */ }
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [tenant_slug]);

  const update = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name) { toast.error('Your name is required'); return; }
    if (!form.parent_email && !form.parent_phone) { toast.error('Please share an email or phone'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/daycare-lead`, {
        tenant_slug,
        source: sourceParam,
        ...form,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return <div style={fullPage}><Loader2 size={32} className="animate-spin" /></div>;
  if (!tenant) return <div style={fullPage}><p>Daycare not found.</p></div>;

  const primary = branding?.primary_color || '#4F46E5';
  const accent = branding?.secondary_color || '#22C55E';
  const name = branding?.brand_name || tenant?.brand_name || tenant?.tenant_name || tenant?.name || 'Our Daycare';
  const logoUrl = branding?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : '';

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#f8fafc 0%, #eff6ff 100%)', padding: '32px 16px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 480px', gap: 32, alignItems: 'start' }} className="enroll-grid">
        {/* Left: Marketing */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            {logoUrl ? (
              <img src={logoUrl} alt={name} style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover' }} />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: 12, background: primary, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 22, fontWeight: 800 }}>
                {(name || 'D').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0 }}>{name}</h1>
              <p style={{ color: '#64748b', margin: 0 }}>Now Enrolling — Schedule your tour</p>
            </div>
          </div>
          <h2 style={{ fontSize: 36, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, margin: '24px 0 16px' }}>
            A Safe, Loving & Bilingual Environment <span style={{ color: primary }}>For Your Child</span>
          </h2>
          <p style={{ color: '#475569', fontSize: 16, lineHeight: 1.6 }}>
            At {name}, we provide a safe, nurturing and bilingual environment where your child can learn, explore, and build a bright future.
          </p>
          <div style={{ display: 'flex', gap: 16, marginTop: 24, flexWrap: 'wrap' }}>
            <Badge icon={<ShieldCheck size={20} color="#fff" />} bg={primary} text="Licensed & Insured" sub="State certified" />
            <Badge icon={<Heart size={20} color="#fff" />} bg="#ec4899" text="CPR Certified" sub="Staff certified" />
            <Badge icon={<Users size={20} color="#fff" />} bg={accent} text="Small Groups" sub="Personalized care" />
          </div>
        </div>

        {/* Right: Form */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 28, boxShadow: '0 10px 40px rgba(15,23,42,.08)', border: '1px solid #e2e8f0' }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }} data-testid="enroll-success">
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
                <CheckCircle2 size={32} color="#16a34a" />
              </div>
              <h3 style={{ color: '#0f172a', fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Thank you!</h3>
              <p style={{ color: '#64748b', margin: 0 }}>We received your request and will contact you very soon. 🌻</p>
              <button onClick={() => { setSubmitted(false); setForm({ parent_name:'', parent_email:'', parent_phone:'', child_name:'', child_age:'', program_interest:'', desired_start_date:'', message:'' }); }} style={{ marginTop: 20, padding: '10px 20px', background: primary, color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                Submit another inquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="enroll-form">
              <h3 style={{ fontSize: 22, color: primary, fontWeight: 700, margin: '0 0 4px' }}>Now Enrolling! ☀️</h3>
              <p style={{ color: '#64748b', margin: '0 0 20px', fontSize: 13 }}>Fill out the form below and we'll contact you soon!</p>
              <Field label="Parent Name *"><input required value={form.parent_name} onChange={update('parent_name')} placeholder="Enter your name" data-testid="enroll-parent-name" /></Field>
              <Field label="Phone Number"><input type="tel" value={form.parent_phone} onChange={update('parent_phone')} placeholder="(617) 555-1234" data-testid="enroll-phone" /></Field>
              <Field label="Email Address"><input type="email" value={form.parent_email} onChange={update('parent_email')} placeholder="name@email.com" data-testid="enroll-email" /></Field>
              <Field label="Child Name"><input value={form.child_name} onChange={update('child_name')} placeholder="Enter child's name" /></Field>
              <Field label="Child Age"><input value={form.child_age} onChange={update('child_age')} placeholder="e.g. 3 years" /></Field>
              <Field label="Program Interest">
                <select value={form.program_interest} onChange={update('program_interest')}>
                  <option value="">Select program</option>
                  {PROGRAMS.map(p => <option key={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Desired Start Date"><input type="date" value={form.desired_start_date} onChange={update('desired_start_date')} /></Field>
              <Field label="Message (Optional)"><textarea rows={3} value={form.message} onChange={update('message')} placeholder="Type your message here..." /></Field>
              <button
                type="submit"
                disabled={submitting}
                data-testid="enroll-submit"
                style={{ width: '100%', padding: 14, marginTop: 8, background: primary, color: '#fff', border: 0, borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? 'Submitting...' : 'Request Information'}
              </button>
              <p style={{ textAlign: 'center', fontSize: 12, color: '#64748b', marginTop: 12 }}>We will contact you soon! ❤️</p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .enroll-grid input, .enroll-grid select, .enroll-grid textarea {
          width: 100%; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px; box-sizing: border-box;
          background: #fff; color: #0f172a;
        }
        .enroll-grid label { display:block; font-size:12px; color:#475569; font-weight:600; margin-bottom:6px; }
        @media (max-width: 920px) { .enroll-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label>{label}</label>
      {children}
    </div>
  );
}

function Badge({ icon, bg, text, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ background: bg, width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center' }}>{icon}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{text}</div>
        <div style={{ fontSize: 11, color: '#64748b' }}>{sub}</div>
      </div>
    </div>
  );
}

const fullPage = { minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f8fafc' };
